/**
 * DebugPanel — FAB flotante + bottom sheet de diagnóstico HTTP.
 * Solo visible en builds de desarrollo (import.meta.env.DEV).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { IS_DEMO_MODE } from '@/config/featureFlags';
import { getDebugEntries, clearDebugEntries, subscribeDebug } from './debugStore';
import type { DebugEntry } from './debugStore';
import './DebugPanel.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const METHOD_CLASS: Record<string, string> = {
  GET: 'dbg-m-get', POST: 'dbg-m-post', PUT: 'dbg-m-put',
  PATCH: 'dbg-m-patch', DELETE: 'dbg-m-delete',
};

const SVC_LABEL: Record<string, string> = {
  survey: 'Survey', analytics: 'Analytics', userAdmin: 'UserAdmin',
  rules: 'Rules', meeting: 'Meeting', notifications: 'Notif',
};

function statusCls(status: number | null): string {
  if (status === null || status >= 500) return 'dbg-s-error';
  if (status >= 400) return 'dbg-s-warn';
  return 'dbg-s-ok';
}

function fmtDur(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map(n => String(n).padStart(2, '0'))
    .join(':');
}

// ── Sub-components ────────────────────────────────────────────────────────────

function JsonViewer({ label, data }: { label: string; data: unknown }) {
  const [collapsed, setCollapsed] = useState(false);
  if (data === null || data === undefined) {
    return (
      <div className="dbg-section">
        <div className="dbg-section-label">{label} <span className="dbg-null">—</span></div>
      </div>
    );
  }
  const str = JSON.stringify(data, null, 2);
  const display = str.length > 8000 ? str.slice(0, 8000) + '\n…(truncado)' : str;
  return (
    <div className="dbg-section">
      <button className="dbg-section-label dbg-section-toggle" onClick={() => setCollapsed(c => !c)}>
        {label}
        <i className={`fas fa-chevron-${collapsed ? 'right' : 'down'}`} />
      </button>
      {!collapsed && <pre className="dbg-json">{display}</pre>}
    </div>
  );
}

function EntryRow({ entry, active, onClick }: { entry: DebugEntry; active: boolean; onClick: () => void }) {
  return (
    <button
      className={`dbg-entry${active ? ' dbg-entry--active' : ''}${entry.isError ? ' dbg-entry--err' : ''}`}
      onClick={onClick}
    >
      <span className={`dbg-method ${METHOD_CLASS[entry.method] ?? ''}`}>{entry.method}</span>
      <span className="dbg-svc">{SVC_LABEL[entry.service] ?? entry.service}</span>
      <span className="dbg-path">{entry.path}</span>
      <span className={`dbg-status ${statusCls(entry.status)}`}>{entry.status ?? 'NET'}</span>
      <span className="dbg-dur">{fmtDur(entry.durationMs)}</span>
      <span className="dbg-time">{fmtTime(entry.timestamp)}</span>
    </button>
  );
}

function DetailPane({ entry }: { entry: DebugEntry | null }) {
  if (!entry) {
    return (
      <div className="dbg-detail-empty">
        <i className="fas fa-arrow-left" />
        Selecciona una llamada
      </div>
    );
  }
  return (
    <div className="dbg-detail-content">
      <div className="dbg-detail-header">
        <span className={`dbg-method ${METHOD_CLASS[entry.method] ?? ''}`}>{entry.method}</span>
        <span className="dbg-detail-url">{entry.url}</span>
        <span className={`dbg-status ${statusCls(entry.status)}`}>{entry.status ?? 'NET'}</span>
        <span className="dbg-dur">{fmtDur(entry.durationMs)}</span>
        <span className="dbg-time">{fmtTime(entry.timestamp)}</span>
      </div>
      <JsonViewer label="Request Body" data={entry.requestBody} />
      <JsonViewer label="Response Body" data={entry.responseBody} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function DebugPanel() {
  if (!IS_DEMO_MODE) return null;
  return <DebugPanelInner />;
}

function DebugPanelInner() {
  const [open, setOpen]         = useState(false);
  const [entries, setEntries]   = useState<readonly DebugEntry[]>(() => getDebugEntries());
  const [selected, setSelected] = useState<DebugEntry | null>(null);
  const listRef                 = useRef<HTMLDivElement>(null);

  useEffect(() => subscribeDebug(() => {
    setEntries(getDebugEntries());
  }), []);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = 0;
  }, [entries.length, open]);

  const handleClear = useCallback(() => {
    clearDebugEntries();
    setSelected(null);
    setEntries([]);
  }, []);

  const errorCount = entries.filter(e => e.isError).length;

  return (
    <>
      {/* ── FAB button ── */}
      <button
        className="dbg-fab"
        onClick={() => setOpen(o => !o)}
        title="API Debug"
        aria-label="Abrir panel de debug"
      >
        <i className="fas fa-bug" aria-hidden="true" />
        {entries.length > 0 && (
          <span className={`dbg-fab-badge${errorCount > 0 ? ' dbg-fab-badge--error' : ''}`}>
            {errorCount > 0 ? errorCount : entries.length}
          </span>
        )}
      </button>

      {/* ── Bottom sheet ── */}
      {open && (
        <>
          <div className="dbg-overlay" onClick={() => setOpen(false)} />
          <div className="dbg-sheet">

            {/* Sheet header */}
            <div className="dbg-sheet-header">
              <i className="fas fa-bug" aria-hidden="true" />
              <span className="dbg-sheet-title">API Debug</span>
              {entries.length > 0 && <span className="dbg-sheet-count">{entries.length} llamadas</span>}
              {errorCount > 0  && <span className="dbg-sheet-errors">{errorCount} errores</span>}
              <div style={{ flex: 1 }} />
              {entries.length > 0 && (
                <button className="dbg-sheet-btn" onClick={handleClear} title="Limpiar">
                  <i className="fas fa-trash-alt" /> Limpiar
                </button>
              )}
              <button className="dbg-sheet-btn dbg-sheet-close" onClick={() => setOpen(false)}>
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Sheet body */}
            <div className="dbg-layout">

              {/* Left: entry list */}
              <div className="dbg-list" ref={listRef}>
                {entries.length === 0
                  ? <div className="dbg-list-empty">Sin llamadas aún — navega por la app</div>
                  : entries.map(e => (
                      <EntryRow
                        key={e.id}
                        entry={e}
                        active={selected?.id === e.id}
                        onClick={() => setSelected(e)}
                      />
                    ))
                }
              </div>

              {/* Right: detail */}
              <div className="dbg-detail">
                <DetailPane entry={selected} />
              </div>

            </div>
          </div>
        </>
      )}
    </>
  );
}
